import memoize from 'fast-memoize';

import { captureException } from '@yoweb/utils/sentry';

// Format style: "Apr 8, 2022"
const defaults = {
  month: 'short',
  year: 'numeric',
  day: 'numeric',
} as const;

// Format style: "April"
const monthOnly = {
  month: 'long',
} as const;

// Format style: "April 2022"
const monthAndYear = {
  month: 'long',
  year: 'numeric',
} as const;

// Format style: "Apr 8"
const shortMonthAndDay = {
  month: 'short',
  day: 'numeric',
} as const;

// Format style: "Sat, Apr 8"
const shortWeekdayMonthAndDay = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
} as const;

// Format style: "Saturday, Apr 8"
const longWeekdayShortMonthAndDay = {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
} as const;

// Format style: "Apr 8, 2022"
const shortMonthYearAndDay = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
} as const;

const timeOnly = {
  hour: 'numeric',
  minute: 'numeric',
} as const;

const timeAndDate = {
  ...shortMonthYearAndDay,
  ...timeOnly,
} as const;

export const Presets = {
  defaults,
  monthOnly,
  monthAndYear,
  shortMonthAndDay,
  shortWeekdayMonthAndDay,
  longWeekdayShortMonthAndDay,
  shortMonthYearAndDay,
  timeOnly,
  timeAndDate,
} as const;

export type FormatDateOptions = {
  locale: string;
  preset?: keyof typeof Presets;
} & Partial<Intl.DateTimeFormatOptions>;

export const getDateTimeFormat = memoize(Intl.DateTimeFormat);

/**
 * Formats a date according to the specified locale settings.
 * Caches the formatter instance per locale.
 */
export const formatDate = (date: string | Date | number, config: FormatDateOptions) => {
  const { preset, locale, ...options } = config;
  const presets = preset && Presets[preset];
  const mergedOptions = { ...presets, ...options };

  const formatter = getDateTimeFormat(locale, mergedOptions);

  return formatter.format(typeof date === 'string' ? new Date(date) : date);
};

// we're defaulting to this year because 2020 is a leap year
// which helps ensure that if the user doesn't provide the year,
// they can still provide the date of "Feb. 29th"
const DEFAULT_YEAR = 2020;

/**
 * Conditionally use different formatDate presets based on the available date info.
 * Month and day are expected to be set for any date to be displayed.
 * Months are expected to be in a human readable format, starting from 1 instead of 0.
 */
export const formatPartialDate = (
  { year, month, day }: { year?: number; month?: number; day?: number },
  config: FormatDateOptions,
) => {
  const sanitizedConfig = { ...config };

  // Note: removing the timeZone as we're not dealing in time and creates off by one results
  delete sanitizedConfig.timeZone;

  try {
    if (!month && !day && year) {
      return `${year}`;
    } else if (year && month && day) {
      return formatDate(new Date(year, month - 1, day), {
        ...sanitizedConfig,
        dateStyle: undefined,
        preset: 'defaults',
      });
    } else if (month && day) {
      return formatDate(new Date(DEFAULT_YEAR, month - 1, day), {
        ...sanitizedConfig,
        dateStyle: undefined,
        preset: 'shortMonthAndDay',
      });
    }

    return '';
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch (e) {
    // Note: this is user input so it is possible they can provide an invalid date
    // which is why we're avoiding the fatal error here
    captureException(e);

    return '';
  }
};

export const isPast = (date: Date) => date.getTime() < Date.now();

// am/pm format
export const getPeriod = (hours: number) => (hours >= 12 && hours < 24 ? 'pm' : 'am');

/**
 * Checks if the date is today.
 */
export const isDateToday = (date: number) => isSameDay(new Date(date), new Date());

/**
 * Strips the time component from a date.
 */
export const stripTimeFromDate = (date: number) => {
  const dateWithTime = new Date(date);

  return new Date(
    dateWithTime.getFullYear(),
    dateWithTime.getMonth(),
    dateWithTime.getDate(),
    0,
    0,
    0,
  ).getTime();
};

/**
 * Checks if a particular date is yesterday.
 */
export const isDateYesterday = (date: number) => {
  // Strip the time component from both dates.
  const today = stripTimeFromDate(new Date().getTime());
  const then = stripTimeFromDate(new Date(date).getTime());

  // If exactly 24 hours have passed between the two dates, we know that the date was yesterday.
  return today - then === 24 * 60 * 60 * 1000;
};

/**
 * Checks if the date is on the same day.
 */
export const isDateSameDay = (date: number, date2: number) =>
  isSameDay(new Date(date), new Date(date2));

const isSameDay = (d1: Date, d2: Date) =>
  d1.getDate() === d2.getDate() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getFullYear() === d2.getFullYear();

/**
 * Checks if the date is within this week.
 */
export const isDateThisWeek = (date: number) => {
  // Strip the time component from both dates.
  const today = stripTimeFromDate(new Date().getTime());
  const then = stripTimeFromDate(new Date(date).getTime());

  // If less than 7 days have passed between the two dates, we know that the date is within the same week.
  // Also check that the difference is larger or equal to 0, since today is within the same week.
  const diff = today - then;
  return diff >= 0 && diff <= 7 * 24 * 60 * 60 * 1000;
};

export const getMinutesBetweenDates = (startDate: number, endDate: number) =>
  (new Date(startDate).getTime() - new Date(endDate).getTime()) / 60000;

export const getMonthsBetweenDates = (dateFrom: Date, dateTo: Date) =>
  dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear());

export const validateTimezone = (timeZone: string | undefined): timeZone is string => {
  if (!timeZone) {
    return false;
  }

  try {
    // call will throw on invalid timeZone value
    new Intl.DateTimeFormat('en', { timeZone }).format(new Date());
    return true;
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch {
    return false;
  }
};

/**
 * Formats a duration in seconds into a time format (H:MM:SS, M:SS, or S).
 * @param {number} durationInSeconds - The duration in seconds to format.
 * @returns {string} The formatted duration in the appropriate time format.
 */
export const formatSecIntoHMS = (durationInSeconds: number) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds - hours * 3600) / 60);
  const seconds = Math.floor(durationInSeconds - hours * 3600 - minutes * 60);

  if (hours === 0 && minutes === 0) {
    return `${seconds}`;
  }
  const secondStr = `${seconds < 10 ? '0' : ''}${seconds}`;

  if (hours === 0) {
    return `${minutes}:${secondStr}`;
  }
  const minuteStr = `${minutes < 10 ? '0' : ''}${minutes}`;
  return `${hours}:${minuteStr}:${secondStr}`;
};
